import { useMemo } from "react";
import { CheckCircle2, XCircle, HelpCircle } from "lucide-react";
import { Input } from "@/components/ui/input";

// Groupement standard de la carte vitale : S YY MM DD CCC OOO KK (15 chiffres)
// -> "1 82 01 37 261 265 32" — 1 sexe + 2 année + 2 mois + 2 département +
//    3 commune + 3 ordre + 2 clé de contrôle = 15
const GROUP_LENGTHS = [1, 2, 2, 2, 3, 3, 2];

/**
 * Formate 15 chiffres bruts en groupes espacés selon GROUP_LENGTHS.
 * Retourne une chaîne partielle en cours de saisie.
 */
export function formatSsn(raw) {
  const digits = (raw || "").replace(/\D/g, "").slice(0, 15);
  if (!digits) return "";
  const parts = [];
  let cursor = 0;
  for (const len of GROUP_LENGTHS) {
    if (cursor >= digits.length) break;
    parts.push(digits.slice(cursor, cursor + len));
    cursor += len;
  }
  return parts.join(" ");
}

/**
 * Retourne un placeholder "1 __ __ __ _____ ___ __" pour montrer le masque.
 */
export function ssnMaskPlaceholder() {
  return GROUP_LENGTHS.map((len) => "_".repeat(len)).join(" ");
}

/**
 * Valide un NIR (numéro de sécurité sociale FR) en fonction de la civilité et de la date de naissance.
 * Retourne { ok, reason }.
 *   - ok=true  : NIR valide (15 chiffres, tous les checks passent)
 *   - ok=false : reason explique pourquoi (utilisable pour un feedback UI)
 *   - ok=null  : NIR incomplet (moins de 15 chiffres) ou aucune erreur bloquante encore détectée
 */
export function validateSsn(rawSsn, civility, birthDateIso = "") {
  const digits = (rawSsn || "").replace(/\D/g, "");
  if (digits.length === 0) return { ok: null, reason: "" };

  // 1) Sexe (1 = homme, 2 = femme)
  const civ = (civility || "").toLowerCase();
  const expectedSex = civ === "monsieur" || civ === "m" || civ === "m." ? "1" : (civ === "madame" || civ === "mademoiselle" || civ === "mme" ? "2" : "");
  if (expectedSex && digits[0] && digits[0] !== expectedSex) {
    return {
      ok: false,
      reason: `Le premier chiffre doit être ${expectedSex} pour ${expectedSex === "1" ? "un homme" : "une femme"}`,
    };
  }

  // 2) Année (positions 1-2) — cohérence avec la date de naissance si renseignée
  if (digits.length >= 3 && birthDateIso) {
    try {
      const yy = digits.slice(1, 3);
      const bYear = new Date(birthDateIso).getUTCFullYear();
      const bYearYY = String(bYear % 100).padStart(2, "0");
      if (!Number.isNaN(bYear) && yy !== bYearYY) {
        return { ok: false, reason: `L'année (${yy}) ne correspond pas à votre date de naissance (${bYearYY})` };
      }
    } catch { /* birthDateIso invalide, ignorer */ }
  }

  // 3) Mois (positions 3-4)
  const VALID_MONTHS = new Set(["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12", "20", "30", "31", "32", "33", "34", "35", "36", "37", "38", "39", "40", "41", "42", "50", "62", "63", "99"]);
  if (digits.length >= 5) {
    const mm = digits.slice(3, 5);
    if (!VALID_MONTHS.has(mm)) {
      return { ok: false, reason: `Le mois "${mm}" n'est pas valide (attendu 01-12, ou 20/30/40/50 pour cas spéciaux)` };
    }
    // Cohérence date de naissance sur le mois
    if (birthDateIso) {
      try {
        const bMonth = String(new Date(birthDateIso).getUTCMonth() + 1).padStart(2, "0");
        if (["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"].includes(mm) && mm !== bMonth) {
          return { ok: false, reason: `Le mois (${mm}) ne correspond pas à votre date de naissance (${bMonth})` };
        }
      } catch { /* ignore */ }
    }
  }

  // 4) Département (positions 5-7) — 3 chiffres, mais les Corse utilisent 2A=19, 2B=18 dans la clé
  // On ne bloque pas ici, on laisse la clé de contrôle valider.

  if (digits.length < 15) {
    return { ok: null, reason: "" }; // saisie en cours, pas de verdict bloquant
  }

  // 5) Clé de contrôle
  const nirBody = digits.slice(0, 13);
  const key = digits.slice(13, 15);
  let numeric = nirBody;
  // Corse : 2A → remplacer par 19, 2B → 18 (dans la section département)
  // Dans un NIR, ce cas n'apparaît pas car les chiffres sont numériques ; la substitution est utile
  // uniquement quand l'utilisateur saisit "2A"/"2B" en clair. Ici digits ne contient que [0-9], donc rien à faire.
  const expectedKey = String(97 - (Number(numeric) % 97)).padStart(2, "0");
  if (key !== expectedKey) {
    return { ok: false, reason: `Clé de contrôle invalide (attendue : ${expectedKey})` };
  }

  return { ok: true, reason: "NIR valide" };
}

/**
 * Composant contrôlé pour la saisie du NIR avec masque + validation temps réel.
 *
 * Props :
 *   - value              : chaîne brute stockée dans le formulaire (peut contenir des espaces, on nettoie)
 *   - onChange(rawDigits): callback à chaque changement — reçoit UNIQUEMENT les chiffres bruts (0-15 chars)
 *   - civility           : "monsieur" | "madame" | "mademoiselle" (contrôle la règle sexe)
 *   - birthDateIso       : YYYY-MM-DD (optionnel, valide la cohérence année/mois)
 *   - required           : marque le label d'un astérisque
 *   - showValidation     : true = affiche l'état inline même sans submit (défaut true)
 *   - testid             : data-testid de l'input
 */
export default function SsnInput({
  value = "",
  onChange,
  civility = "",
  birthDateIso = "",
  required = false,
  showValidation = true,
  testid = "ssn-input",
}) {
  const formatted = useMemo(() => formatSsn(value), [value]);
  const digits = (value || "").replace(/\D/g, "").slice(0, 15);
  const result = useMemo(() => validateSsn(digits, civility, birthDateIso), [digits, civility, birthDateIso]);

  const handleChange = (e) => {
    const cleaned = e.target.value.replace(/\D/g, "").slice(0, 15);
    onChange?.(cleaned);
  };

  const borderCls = !showValidation || digits.length === 0
    ? "border-gray-300"
    : result.ok === true
      ? "border-green-500 ring-1 ring-green-200"
      : result.ok === false
        ? "border-red-500 ring-1 ring-red-300"
        : "border-gray-300";

  return (
    <div>
      <div className="relative">
        <Input
          value={formatted}
          onChange={handleChange}
          placeholder={ssnMaskPlaceholder()}
          inputMode="numeric"
          autoComplete="off"
          spellCheck={false}
          className={`rounded-none pr-9 font-mono tracking-wider text-sm ${borderCls}`}
          data-testid={testid}
          aria-required={required}
          aria-invalid={result.ok === false}
        />
        {showValidation && digits.length > 0 && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2" data-testid={`${testid}-status`} aria-hidden="true">
            {result.ok === true && <CheckCircle2 className="w-4 h-4 text-green-600" />}
            {result.ok === false && <XCircle className="w-4 h-4 text-red-600" />}
            {result.ok === null && <HelpCircle className="w-4 h-4 text-gray-300" />}
          </div>
        )}
      </div>
      {showValidation && (
        <div className="flex items-center justify-between mt-1 min-h-[16px]">
          <p
            className={`text-[11px] ${result.ok === false ? "text-red-600" : result.ok === true ? "text-green-700" : "text-gray-400"}`}
            data-testid={`${testid}-message`}
            role={result.ok === false ? "alert" : undefined}
          >
            {result.ok === false ? result.reason : result.ok === true ? "✓ NIR valide" : `${digits.length}/15 chiffres`}
          </p>
        </div>
      )}
    </div>
  );
}
